import { useState, useEffect, SyntheticEvent } from 'react';

import {
  AppBar,
  Tabs,
  Tab,
  Grid,
  Typography,
  Link,
  Snackbar,
  Alert,
  AlertColor,
} from '@mui/material';

import SnapshotAlarmSetupTabPanel from './SnapshotAlarmSetupTabPanel';

import AlarmSettingsSetup from './AlarmSettingsSetup';
import NotificationsSetup from './NotificationsSetup';
import EmailSetup from './EmailSetup';
import SnmpSetup from './SnmpSetup';

import {
  clearEmailTestStatus,
  testEmail,
} from '../spectrasWebSocketServer/spectrasWebSocketServerSlice';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { SnapshotAlarmSettingForm } from './types';
import { testSnmp } from 'features/spectrasWebSocketServer/spectrasWebSocketServerSlice';
import { ModuleType } from '../analyzerModules/types';
import { useForm } from 'react-hook-form';
import { restoreSettingsDefaults } from './utils/formHelpers';

import { useAlarmSettings } from 'features/alarms/api/getAlarmSettings';
import { useCreateAlarmSettings } from 'features/alarms/api/createAlarmSettings';
import { useUpdateAlarmSettings } from 'features/alarms/api/updateAlarmSettings';
import { useGetAnalyzersByModule } from 'features/analyzers/hooks/useGetAnalyzersByModule';

const SnapshotAlarmSetupPage = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const [selectedTab, setSelectedTab] = useState<0 | 1 | 2 | 3>(0);
  const {
    control,
    register,
    handleSubmit,
    getValues,
    reset,
    formState: { isDirty, errors },
  } = useForm<SnapshotAlarmSettingForm>({ mode: 'onChange' });
  const analyzersByModule = useGetAnalyzersByModule(ModuleType.BASE);

  const emailTestSuccess = useAppSelector(
    (state) => state.spectrasWebSocketServer.emailTestSuccess
  );
  const [snackbarState, setSnackbarState] = useState<{
    open: boolean;
    message: string;
    severity: AlertColor | undefined;
  }>({
    open: false,
    message: '',
    severity: undefined,
  });

  const { data: alarmSettings } = useAlarmSettings({});
  const createAlarmSettingsMutation = useCreateAlarmSettings({});
  const updateAlarmSettingsMutation = useUpdateAlarmSettings({});

  useEffect(() => {
    if (!alarmSettings) {
      reset({ ignore_interval: 5, raising_count: 3000 });
    } else {
      const settingsForm = restoreSettingsDefaults(alarmSettings);
      reset(settingsForm);
    }
  }, [reset, alarmSettings]);

  const handleTabChanged = (event: SyntheticEvent, newValue: any) => {
    setSelectedTab(newValue);
  };

  const [emailRecipients, setEmailRecipients] = useState<string[]>([]);
  // state to track changes in email recipients list
  const [emailRecipientsHasChanges, setEmailRecipientsHasChanges] =
    useState(false);
  const [currentEmail, setCurrentEmail] = useState<string>('');
  const isValidEmail = (email: string) => {
    const emailRegex =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return emailRegex.test(email);
  };
  const onSubmit = async () => {
    // Include currentEmail if it's a valid email
    if (currentEmail.trim() !== '' && isValidEmail(currentEmail.trim())) {
      emailRecipients.push(currentEmail.trim());
      setCurrentEmail('');
    }

    const alarmSettings = getValues();
    //Include all email recipients in the alarmSettings object
    alarmSettings.email_recipient =
      emailRecipients.length > 0 ? emailRecipients.join('; ') : '';
    // If no alarm settings exist in DB, create an entry
    if (!alarmSettings?.id) {
      if (!alarmSettings) return;
      const savedSettings = await createAlarmSettingsMutation.mutateAsync({
        data: alarmSettings,
      });
      reset(savedSettings);
      // Otherwise, update existing entry
    } else {
      if (!emailRecipientsHasChanges) return;
      const updatedSettings = await updateAlarmSettingsMutation.mutateAsync({
        data: alarmSettings,
        alarmSettingId: alarmSettings.id,
      });
      reset(updatedSettings);
      setEmailRecipientsHasChanges(false); // Reset the hasChanges to false after a successful submission
    }
  };

  const handleTestEmail = () => {
    const alarmSettings = getValues();
    if (!alarmSettings || !user?.id) return;
    const emailSettings = {
      EmailSmtp: alarmSettings.email_smtp,
      EmailPort: alarmSettings.email_port || 465,
      EmailUserName: alarmSettings.email_username,
      EmailPassword: alarmSettings.email_password,
      EmailFrom: alarmSettings.email_from,
      EmailRecipient: alarmSettings.email_recipient,
      EmailSubject: alarmSettings.email_subject,
      EmailMessage: alarmSettings.email_message,
    };
    dispatch(
      testEmail({
        email_settings: emailSettings,
        user_id: user.id,
      })
    );
  };

  const handleTestSnmp = () => {
    const snmpSettings = getValues();
    dispatch(testSnmp(snmpSettings));
  };

  useEffect(() => {
    if (emailTestSuccess === undefined) return;
    const message = emailTestSuccess
      ? 'Test Message Sent Successfully'
      : 'Test Message Failed to Send';
    const severity = emailTestSuccess ? 'success' : 'error';

    setSnackbarState({
      open: true,
      message: message,
      severity: severity,
    });
    dispatch(clearEmailTestStatus());
  }, [emailTestSuccess, dispatch]);

  if (analyzersByModule?.length === 0) {
    return (
      <Grid container>
        <Typography>
          To configure alarms, please add analyzers with purchased BASE or GEO
          modules. Contact{' '}
          <Link href='mailto:sales@lptech.com'>LP Technologies</Link> for more
          information.
        </Typography>
      </Grid>
    );
  }

  return (
    <>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={snackbarState.open}
        autoHideDuration={2000}
        onClose={() => setSnackbarState({ ...snackbarState, open: false })}
      >
        <Alert
          onClose={() => setSnackbarState({ ...snackbarState, open: false })}
          severity={snackbarState.severity}
        >
          {snackbarState.message}
        </Alert>
      </Snackbar>
      <AppBar position='static' style={{ borderRadius: '10px' }}>
        <Tabs
          value={selectedTab}
          onChange={handleTabChanged}
          sx={{
            backgroundColor: (theme) =>
              theme.palette.mode !== 'dark' ? 'white' : undefined,
          }}
        >
          <Tab label='Settings' />
          <Tab label='Notifications' />
          <Tab label='Email' />
          <Tab label='SNMP' />
        </Tabs>
      </AppBar>
      <SnapshotAlarmSetupTabPanel value={selectedTab} index={0}>
        <AlarmSettingsSetup />
      </SnapshotAlarmSetupTabPanel>
      <SnapshotAlarmSetupTabPanel value={selectedTab} index={1}>
        <NotificationsSetup
          errors={errors}
          control={control}
          handleSubmit={() => {
            handleSubmit(onSubmit)();
          }}
          hasChanges={isDirty}
        />
      </SnapshotAlarmSetupTabPanel>
      <SnapshotAlarmSetupTabPanel value={selectedTab} index={2}>
        <EmailSetup
          control={control}
          register={register}
          onSubmit={onSubmit}
          emailRecipients={emailRecipients}
          setEmailRecipients={setEmailRecipients}
          handleTestEmail={handleTestEmail}
          hasChanges={isDirty || emailRecipientsHasChanges}
          setHasChanges={setEmailRecipientsHasChanges}
          errors={errors}
          currentEmail={currentEmail}
          setCurrentEmail={setCurrentEmail}
        />
      </SnapshotAlarmSetupTabPanel>
      <SnapshotAlarmSetupTabPanel value={selectedTab} index={3}>
        <SnmpSetup
          control={control}
          handleSubmit={() => {
            handleSubmit(onSubmit)();
          }}
          hasChanges={isDirty}
          handleTestSnmp={handleTestSnmp}
          errors={errors}
        />
      </SnapshotAlarmSetupTabPanel>
    </>
  );
};

export default SnapshotAlarmSetupPage;
